import { isValid } from "date-fns";

import { AddonCart, Coupon, Location } from "store/types";

import {
  AddonCardData,
  AddonsFormData,
  SjdBookingInlineData,
} from "./global-types";

export const reduceIconPath = (path: string): string => {
  // Full URL → foreign asset (e.g. a hub-hosted addon image). Pass through
  // as-is; never prepend the local plugin dir.
  if (/^https?:\/\//i.test(path)) return path;

  const pluginDirUrl = (window as SjdBookingInlineData).sjd_booking_dir_url;
  return `${pluginDirUrl}app/build/images/${path.replace(/^\//, "")}`;
};

// Characters that WordPress's remove_accents() transliterates but that the
// Unicode NFD decomposition CAN'T handle (they have no combining form).
// Kept in sync with the most common entries from
// wp-includes/formatting.php → remove_accents(). Not exhaustive — if the
// hub starts producing filenames with more exotic characters we should add
// them here, but the practical rule is still to stick to alphanumeric +
// hyphens + underscores on the hub side so divergence from PHP's full
// table never actually matters.
const NON_COMBINING_TRANSLITERATIONS: Record<string, string> = {
  // German
  ß: "ss",
  // Ligatures
  Æ: "AE",
  æ: "ae",
  Œ: "OE",
  œ: "oe",
  Ĳ: "IJ",
  ĳ: "ij",
  // Polish
  Ł: "L",
  ł: "l",
  // Nordic / Baltic
  Ø: "O",
  ø: "o",
  Đ: "D",
  đ: "d",
  Ð: "D",
  ð: "d",
  Þ: "TH",
  þ: "th",
  // Misc
  Ə: "E",
  ə: "e",
  "€": "E",
};

const applyNonCombiningTransliterations = (input: string): string => {
  let result = "";
  for (const ch of input) {
    result += NON_COMBINING_TRANSLITERATIONS[ch] ?? ch;
  }
  return result;
};

export const sanitizeFileName = (raw: string): string => {
  if (!raw) return "";

  // First pass: transliterate chars that have no combining-diacritic form
  // and won't be reached by NFD decomposition (ß, Ł, Æ, …).
  let out = applyNonCombiningTransliterations(raw);

  // remove_accents equivalent for combining diacritics (é → e, ñ → n, …).
  out = out.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

  // Non-breaking space → regular space
  out = out.replace(/\u00a0/g, " ");

  // Strip WordPress's $special_chars set (includes %, which is why %20
  // never actually matches the next line — same quirk as core)
  out = out.replace(/[?[\]/\\=<>:;,'"&$#*()|~`!{}%+’«»”“]/g, "");

  // %20 and + would normally collapse to dash, but % is already gone;
  // this still catches literal + signs.
  out = out.replace(/(%20|\+)/g, "-");

  // Collapse whitespace/dashes into a single dash
  out = out.replace(/[\r\n\t -]+/g, "-");

  // Trim leading/trailing . - _
  out = out.replace(/^[.\-_]+|[.\-_]+$/g, "");

  return out;
};

export const resolveVehicleImagePath = (
  picturePath?: string | null
): string => {
  if (!picturePath) return "";

  const trimmedPath = String(picturePath).trim();
  if (!trimmedPath) return "";

  // Pass through data URIs unchanged
  if (/^data:/i.test(trimmedPath)) return trimmedPath;

  // Case 1: Full URL from the hub → localized copy in sub-site uploads
  if (/^https?:\/\//i.test(trimmedPath)) {
    const lastSegment = trimmedPath.split("/").pop();
    if (!lastSegment) return "";
    const fileName = sanitizeFileName(lastSegment);
    return fileName
      ? `/wp-content/uploads/sjd-booking/vehicle-images/${fileName}`
      : "";
  }

  const normalizedPath = trimmedPath.replace(/^\/+/, "");

  // Defensive: if something upstream already stored the sub-site cache
  // path directly, keep it pointing there rather than re-routing to
  // plugin assets.
  if (
    normalizedPath.includes("wp-content/uploads/sjd-booking/vehicle-images/")
  ) {
    const fileName = sanitizeFileName(normalizedPath.split("/").pop() || "");
    return fileName
      ? `/wp-content/uploads/sjd-booking/vehicle-images/${fileName}`
      : "";
  }

  // Case 2: Bare filename (or nested relative path) → plugin-bundled asset
  return reduceIconPath(`cars/${normalizedPath}`);
};

export const getMountPoint = (): HTMLElement | null | undefined =>
  document
    ?.querySelector("sjd-form")
    ?.shadowRoot?.querySelector("#sjd-form-root");

export const getTokenFromUrl = (): string | null => {
  const url = new URL(window.location.href);

  const queryToken = url.searchParams.get("token");
  if (queryToken) return queryToken;

  const pathParts = url.pathname.split("/");
  const last = pathParts[pathParts.length - 1];

  if (
    last &&
    last !== "book-now" &&
    !last.startsWith("step-") &&
    !/^\d+$/.test(last)
  ) {
    return last;
  }

  return null;
};

export const locationsMatchingSession = ({
  formDestination,
  formLocation,
  sessionLocation,
  sessionDestination,
  sessionIsRoundTrip,
  formIsRoundTrip,
}: {
  formDestination: Location | null;
  formLocation: Location | null;
  sessionLocation?: Location;
  sessionDestination?: Location;
  sessionIsRoundTrip?: boolean;
  formIsRoundTrip?: boolean;
}): boolean => {
  const allValueIsDefined: boolean =
    !!formDestination?.location_id &&
    !!sessionDestination?.location_id &&
    !!formLocation?.location_id &&
    !!sessionLocation?.location_id &&
    sessionIsRoundTrip !== undefined &&
    typeof formIsRoundTrip === "boolean";

  if (!allValueIsDefined) return false;

  return (
    String(formDestination?.location_id) ===
      String(sessionDestination?.location_id) &&
    String(formLocation?.location_id) ===
      String(sessionLocation?.location_id) &&
    Boolean(sessionIsRoundTrip) === Boolean(formIsRoundTrip)
  );
};

export const pickFirst = <T>(values: T[], type = ""): T => {
  if (type === "arrays") {
    const [value1, value2] = values;

    if (Array.isArray(value1) && value1.length) {
      return value1;
    }
    return value2;
  }

  const [value1] = values;

  if (value1 !== undefined && value1 !== null) {
    return value1;
  }
  return values[1];
};

export const calculateAddonPrices = (addons: AddonCart[]): number => {
  return addons.reduce((prev: number, next: AddonCart) => {
    if (!next.count) return prev;
    return prev + Number(next.count) * Number(next.price);
  }, 0);
};

export const findPriceForAddons = (
  formAddons: AddonsFormData[],
  reduxAddonList: AddonCardData[],
  isRoundTrip: boolean
) => {
  if (!formAddons || !Object.keys(formAddons).length || !reduxAddonList?.length)
    return [];

  const resultArray = [];

  for (const key in formAddons) {
    const formAddon = formAddons[key];
    const searchId = formAddon.addon_id;

    const reduxAddon = reduxAddonList.find((rdAddon) => {
      return Number(rdAddon.addon_id) === Number(searchId);
    });

    if (reduxAddon) {
      if (reduxAddon.round_trip_price && isRoundTrip) {
        Object.assign(formAddon, {
          price: Number(reduxAddon.round_trip_price),
          name: reduxAddon.name,
        });
      } else if (reduxAddon.price) {
        Object.assign(formAddon, {
          price: Number(reduxAddon.price),
          name: reduxAddon.name,
        });
      }
    }

    resultArray.push(formAddon);
  }

  return resultArray;
};

export const reduceCouponToFlatValue = (
  { amount, coupon_id, discount_type }: Coupon,
  displayingPrice: number
): number => {
  if (!amount || !coupon_id || !discount_type) return 0;

  if (discount_type === "percentage") {
    return (displayingPrice / 100) * Number(amount);
  } else if (discount_type === "flat") {
    return Number(amount);
  }

  return 0;
};

export const reduceToDate = (
  value: Date | null | undefined | string
): Date | undefined => {
  if (!value) return undefined;
  if (!isValid(new Date(value))) return undefined;
  return new Date(new Date(value));
};

export const getDefaultVariantName = ({
  departureIsAirport,
  arrivalIsAirport,
  isRoundTrip,
}: {
  departureIsAirport: boolean;
  arrivalIsAirport: boolean;
  isRoundTrip?: boolean;
}):
  | "bothIsAirport"
  | "destinationIsAirport"
  | "locationIsAirport"
  | "noneIsAirport"
  | "roundTrip" => {
  if (isRoundTrip) {
    return "roundTrip";
  } else if (departureIsAirport && arrivalIsAirport) {
    return "bothIsAirport";
  } else if (departureIsAirport && !arrivalIsAirport) {
    return "destinationIsAirport";
  } else if (!departureIsAirport && arrivalIsAirport) {
    return "locationIsAirport";
  } else if (!departureIsAirport && !arrivalIsAirport) {
    return "noneIsAirport";
  }

  return "noneIsAirport";
};
